// components/Shared/EventBar.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { OverlayTrigger } from 'react-bootstrap';
import { CategoryColor, IEvent } from '../core/type';
import { getEventCategoryColor, isMultiDayEvent, renderTooltip } from '../utils';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { getEventDates, normalizeDate } from '../core/_utility';

// Create a singleton ref for tracking title display across components
const globalTitleDisplayedRef = new Map<string, Set<string>>();

interface EventBarProps {
    event: IEvent & { lane?: number };
    day: Date;
    lane: number;
    rowHeight: number;
    isFirstDay: boolean;
    isLastDay: boolean;
    onEventClick: (e: React.MouseEvent, event: IEvent) => void;
    viewType: 'month' | 'week' | 'day';
    currentDate: Date;
    showArrowIndicators?: boolean;
    titleDisplayedRef: Set<string>;
}

export const EventBar: React.FC<EventBarProps> = React.memo(({
    event,
    day,
    lane,
    rowHeight,
    isFirstDay,
    isLastDay,
    onEventClick,
    viewType,
    currentDate,
    showArrowIndicators = true,
    titleDisplayedRef,
}) => {
    const { categoryAndColorsData }: {
        categoryAndColorsData: CategoryColor[]
    } = useSelector((state: RootState) => state.eventReducer);

    // Memoize the week calculation
    const getWeekOfYear = (date: Date): number => {
        const startOfYear = new Date(date.getFullYear(), 0, 1);
        const days = Math.floor((date.getTime() - startOfYear.getTime()) / 86400000);
        return Math.ceil((days + startOfYear.getDay() + 1) / 7);
    }

    // Memoize date objects to avoid recalculation
    const currentDateObj = useMemo(() => {
        const date = normalizeDate(day);
        return date;
    }, [day]);

    // Get event dates with memoization
    const eventDates = useMemo(() => {
        const { start, end } = getEventDates(event);;
        const startDate = normalizeDate(start);
        const endDate = normalizeDate(end);
        return { startDate, endDate };
    }, [event, getEventDates]);

    // Create key for title tracking based on view type
    const getEventKey = useCallback(() => {
        if (viewType === 'month') {
            return `${event.id}-${currentDate.getMonth()}-${currentDate.getFullYear()}`;
        }
        const weekOfYear = getWeekOfYear(day);
        return `${event.id}-${currentDate.getFullYear()}-week-${weekOfYear}`;
    }, [event.id, viewType, currentDate, day, getWeekOfYear]);

    // Clean up old refs when date changes
    useEffect(() => {
        const refKey = `${viewType}-${currentDate.getFullYear()}-${currentDate.getMonth()}`;
        return () => {
            if (globalTitleDisplayedRef.has(refKey)) {
                globalTitleDisplayedRef.delete(refKey);
            }
        };
    }, [currentDate, viewType]);

    const shouldShowTitle = useMemo(() => {
        const isMultiDay = isMultiDayEvent(event);
        if (!isMultiDay) return true;

        const eventKey = getEventKey();

        // For month view
        if (viewType === 'month') {
            const weekStart = normalizeDate(day);
            weekStart.setDate(day.getDate() - day.getDay());

            const isEventInThisWeek = currentDateObj >= eventDates.startDate;
            const isFirstDayOfWeek = currentDateObj.getTime() === weekStart.getTime();

            if (isEventInThisWeek && (isFirstDay || isFirstDayOfWeek)) {
                titleDisplayedRef.add(eventKey);
                return true;
            }
            return false;
        }

        // For week view
        const weekStart = normalizeDate(day);
        weekStart.setDate(day.getDate() - day.getDay());

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const isEventStartingThisWeek = eventDates.startDate >= weekStart && eventDates.startDate <= weekEnd;
        const isEventContinuingIntoWeek = eventDates.startDate < weekStart && currentDateObj >= weekStart;
        const notYetShownThisWeek = !titleDisplayedRef.has(eventKey);

        if (notYetShownThisWeek) {
            if (isEventStartingThisWeek && isFirstDay) {
                titleDisplayedRef.add(eventKey);
                return true;
            }
            if (isEventContinuingIntoWeek && currentDateObj.getTime() === weekStart.getTime()) {
                titleDisplayedRef.add(eventKey);
                return true;
            }
        }

        return false;
    }, [event, day, viewType, titleDisplayedRef, getEventKey, isFirstDay, currentDateObj, eventDates.startDate]);

    // Memoize category color
    const categoryColor = useMemo(() =>
        getEventCategoryColor(event.category, categoryAndColorsData),
        [event.category, categoryAndColorsData]
    );

    // Memoize styles to prevent recalculation on every render
    const style: React.CSSProperties = useMemo(() => {
        const baseStyle: React.CSSProperties = {
            position: 'absolute',
            top: `${lane * rowHeight}px`,
            left: isFirstDay ? (viewType === 'month' ? '8px' : '4px') : '-1px',
            right: isLastDay ? (viewType === 'month' ? '8px' : '4px') : '-1px',
            backgroundColor: `${categoryColor}30`,
            borderTop: `1px solid ${categoryColor}`,
            borderBottom: `1px solid ${categoryColor}`,
            borderLeft: isFirstDay ? `3px solid ${categoryColor}` : 'none',
            borderRight: isLastDay ? `3px solid ${categoryColor}` : 'none',
            borderRadius: isFirstDay ? '6px 0 0 6px' : isLastDay ? '0 6px 6px 0' : '0',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
        };

        // Adjust justification based on position and title visibility
        if (isFirstDay) {
            baseStyle.justifyContent = 'flex-start';
            baseStyle.paddingLeft = '6px';
        } else if (isLastDay && !isFirstDay) {
            baseStyle.justifyContent = 'flex-end';
            baseStyle.paddingRight = '6px';
        } else if (shouldShowTitle) {
            baseStyle.justifyContent = 'flex-start';
            baseStyle.paddingLeft = '6px';
        } else {
            baseStyle.justifyContent = 'center';
        }

        return baseStyle;
    }, [lane, rowHeight, isFirstDay, isLastDay, viewType, categoryColor, shouldShowTitle]);

    // Determine display content
    const displayContent = useMemo(() => {
        const truncatedTitle = event.title;

        // For Day View - always show full title on first day
        if (viewType === 'day') {
            if (isFirstDay) return `↗ ${truncatedTitle}`;
            if (isLastDay) return `${truncatedTitle} ↖`;
            return `⋯ ${truncatedTitle}`;
        }

        // For Month and Week Views
        if (shouldShowTitle && isFirstDay && showArrowIndicators) {
            return `↗ ${truncatedTitle}`;
        }
        if (shouldShowTitle) {
            return truncatedTitle;
        }
        if (isLastDay && showArrowIndicators) {
            return '↖';
        }
        if (!isFirstDay && !isLastDay && !shouldShowTitle && showArrowIndicators) {
            return '⋯';
        }
        if (!shouldShowTitle && !showArrowIndicators) {
            return '';
        }

        return '';
    }, [event.title, viewType, isFirstDay, isLastDay, shouldShowTitle, showArrowIndicators]);

    // Memoize tooltip to prevent unnecessary re-renders
    const tooltip = useMemo(() => {
        return renderTooltip(event, categoryAndColorsData);
    }, [event, categoryAndColorsData]);

    // Memoize click handler
    const handleClick = useCallback((e: React.MouseEvent) => {
        onEventClick(e, event);
    }, [onEventClick, event]);

    return (
        <OverlayTrigger overlay={tooltip}>
            <div
                onClick={handleClick}
                title={shouldShowTitle ? event.title : undefined}
                className='fs-8'
                style={style}
            >
                {displayContent}
            </div>
        </OverlayTrigger>
    );
});

EventBar.displayName = 'EventBar';